// Frontend feature flag hook — kontroluje či má aktuálny tenant zapnutý daný feature.
//
// Použitie:
//   const hasResale = useFeature('resale_detection');
//   {hasResale && <ResaleClassificationPanel />}
//
// Tenant features sa cachuje v sessionStorage po prvom načítaní (cez fetchTenantFeatures).
// Toto sa volá v Dashboard.tsx pri jeho mount.

import { useState, useEffect } from 'react';

export type FeatureName = 'resale_detection' | 'flowii_integration' | 'omega_export' | 'invoicing' | 'orders' | 'peppol' | 'finance_overview';

const STORAGE_KEY = 'tenant_features';

// In-memory cache + storage cache pre rýchle čítanie naprieč komponentmi
let cachedFeatures: Record<string, boolean> | null = null;
const listeners: Set<() => void> = new Set();

/**
 * Aktualizuj cache (volá Dashboard po načítaní /tenant-meta).
 */
export function setTenantFeatures(features: Record<string, boolean>) {
  cachedFeatures = features || {};
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cachedFeatures));
  } catch {
    // sessionStorage môže byť disabled (private mode)
  }
  listeners.forEach(l => l());
}

/**
 * Vyčisti cache pri logout-e.
 */
export function clearTenantFeatures() {
  cachedFeatures = {};
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
  listeners.forEach(l => l());
}

function getCurrentFeatures(): Record<string, boolean> {
  if (cachedFeatures !== null) return cachedFeatures;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      cachedFeatures = JSON.parse(raw);
      return cachedFeatures || {};
    }
  } catch {}
  return {};
}

/**
 * React hook: vráti `true` ak má aktuálny tenant feature zapnutý.
 */
export function useFeature(feature: FeatureName): boolean {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick(t => t + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  return getCurrentFeatures()[feature] === true;
}
